import { DishIngredient, GetAllResolvedEdiblesResponse } from "api-types";

import React from "react";

import { isIngredientRemovable } from "../../../utils/isIngredientRemovable";
import { OptionalIngredientDetail } from "./OptionalIngredientDetail";

/**
 * Props used to determine which ingredients can be removed from a dish,
 * and display them with the appropriate status.
 */
interface RenderIngredientDetailsParams {
  matchedIngredients: DishIngredient[];
  linkedDish: GetAllResolvedEdiblesResponse["dishes"][string] | undefined;
  linkedItems: GetAllResolvedEdiblesResponse | undefined;
  optionalProductIds: string[] | undefined;
  optionalIngredientIds: string[] | undefined;
}

/**
 * Renders ingredient details showing whether each one can be removed.
 * Used in the Guest Menu drawer when filtering by allergens or ingredients.
 */
export function renderIngredientDetails({
  matchedIngredients,
  linkedDish,
  linkedItems,
  optionalProductIds,
  optionalIngredientIds,
}: RenderIngredientDetailsParams): JSX.Element[] | null {
  if (!matchedIngredients?.length) return null;

  return matchedIngredients.map((ingredient) => {
    const isOptional = isIngredientRemovable(ingredient, optionalIngredientIds);

    const status = isOptional ? "removable" : "not_removable";

    // The optional products a guest would ask to leave out to avoid this ingredient.
    const directOptionalProductIds = (optionalProductIds ??
      linkedDish?.optionalProductIds ??
      []) as string[];
    const sourceProducts = isOptional
      ? Array.from(
          new Set(
            directOptionalProductIds
              .map((pid) => linkedItems?.products?.[pid])
              .filter((p): p is NonNullable<typeof p> => !!p)
              .filter((p) =>
                (p.allIngredients ?? []).some(
                  (i) =>
                    i.igfdTaxIngredientId === ingredient.igfdTaxIngredientId
                )
              )
              .map((p) => p.name)
              .filter((name): name is string => !!name)
          )
        )
      : [];

    return (
      <OptionalIngredientDetail
        key={ingredient.igfdTaxIngredientId}
        ingredient={ingredient}
        status={status}
        sources={sourceProducts}
      />
    );
  });
}
